import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bot, Loader2, Sparkles, Check, X, RefreshCw, BookOpen } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal "Generar preguntas con Genesis AI" para Pruebas y Examenes.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange(open: boolean)
 *  - claseId: string
 *  - claseNombre: string
 *  - onAccept(preguntasSeleccionadas: array) => void   // se llaman con las preguntas que el profe aprobó
 */
export default function GenesisGenerarPreguntas({ open, onOpenChange, claseId, claseNombre, onAccept }) {
  const [step, setStep] = useState('form'); // 'form' | 'preview'
  const [loading, setLoading] = useState(false);
  const [tema, setTema] = useState('');
  const [cantidad, setCantidad] = useState(5);
  const [tipos, setTipos] = useState({ multiple: true, verdadero_falso: true, desarrollo: false });
  const [dificultad, setDificultad] = useState('media');
  const [puntaje, setPuntaje] = useState(5);
  const [preguntas, setPreguntas] = useState([]);
  const [seleccionadas, setSeleccionadas] = useState({}); // id -> true
  const [fuenteLibros, setFuenteLibros] = useState([]);

  const resetAll = () => {
    setStep('form');
    setTema('');
    setCantidad(5);
    setTipos({ multiple: true, verdadero_falso: true, desarrollo: false });
    setDificultad('media');
    setPuntaje(5);
    setPreguntas([]);
    setSeleccionadas({});
    setFuenteLibros([]);
  };

  const handleClose = (val) => {
    if (!val) resetAll();
    onOpenChange(val);
  };

  const tiposArray = () => Object.keys(tipos).filter((t) => tipos[t]);

  const handleGenerar = async () => {
    if (!tema.trim()) { toast.error('Indicá un tema o lección'); return; }
    if (tiposArray().length === 0) { toast.error('Elegí al menos un tipo de pregunta'); return; }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/pruebas-examenes/generar-preguntas-ia`,
        {
          clase_id: claseId,
          tema: tema.trim(),
          cantidad: Number(cantidad),
          tipos: tiposArray(),
          dificultad,
          puntaje: Number(puntaje),
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, timeout: 180000 }
      );
      const lista = res.data?.preguntas || [];
      if (lista.length === 0) {
        toast.error('La IA no devolvió preguntas. Probá reformular el tema.');
        return;
      }
      setPreguntas(lista);
      setFuenteLibros(res.data?.fuente_libros || []);
      const sel = {};
      lista.forEach((p) => { sel[p.id] = true; });
      setSeleccionadas(sel);
      setStep('preview');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error generando preguntas');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerarUna = async (idx) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/pruebas-examenes/generar-preguntas-ia`,
        {
          clase_id: claseId,
          tema: `${tema.trim()} — variante distinta de la anterior`,
          cantidad: 1,
          tipos: [preguntas[idx].tipo],
          dificultad,
          puntaje: Number(puntaje),
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, timeout: 180000 }
      );
      const nuevas = res.data?.preguntas || [];
      if (nuevas.length === 0) { toast.error('No se pudo regenerar'); return; }
      const nuevaPreg = nuevas[0];
      const oldId = preguntas[idx].id;
      const updated = [...preguntas];
      updated[idx] = nuevaPreg;
      setPreguntas(updated);
      setSeleccionadas((s) => {
        const ns = { ...s };
        delete ns[oldId];
        ns[nuevaPreg.id] = true;
        return ns;
      });
      toast.success('Pregunta regenerada');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error regenerando');
    } finally {
      setLoading(false);
    }
  };

  const updatePregunta = (idx, patch) => {
    const updated = [...preguntas];
    updated[idx] = { ...updated[idx], ...patch };
    setPreguntas(updated);
  };

  const updateOpcion = (idx, opIdx, texto) => {
    const updated = [...preguntas];
    const ops = [...(updated[idx].opciones || [])];
    ops[opIdx] = { ...ops[opIdx], texto };
    updated[idx] = { ...updated[idx], opciones: ops };
    setPreguntas(updated);
  };

  const handleAgregar = () => {
    const elegidas = preguntas.filter((p) => seleccionadas[p.id]);
    if (elegidas.length === 0) {
      toast.error('Marcá al menos una pregunta para agregar');
      return;
    }
    onAccept(elegidas);
    resetAll();
    onOpenChange(false);
  };

  const totalSeleccionadas = Object.values(seleccionadas).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="genesis-gen-modal">
        <DialogHeader className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 -m-6 p-6 rounded-t-lg mb-4">
          <DialogTitle className="text-white text-2xl font-bold flex items-center gap-3">
            <Bot className="w-8 h-8" />
            Generar Preguntas con Genesis AI
          </DialogTitle>
          <p className="text-indigo-100 text-sm mt-1">
            Clase: <strong>{claseNombre}</strong>
            {fuenteLibros.length > 0 && (
              <span className="ml-2"> · Libro: <em>{fuenteLibros.join(', ')}</em></span>
            )}
          </p>
        </DialogHeader>

        {step === 'form' && (
          <div className="space-y-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-sm text-indigo-900">
              <BookOpen className="inline w-4 h-4 mr-1" />
              Genesis usará los libros vinculados a esta clase para generar preguntas reales del material.
              Si no hay libros cargados, pedile al admin que los agregue en <strong>Libros Genesis AI</strong>.
            </div>

            <div>
              <Label htmlFor="tema">Tema o lección *</Label>
              <Textarea
                id="tema"
                value={tema}
                onChange={(e) => setTema(e.target.value)}
                placeholder='Ej: "Lección 15", "capítulos 3 a 5", "el liderazgo según el libro"...'
                className="mt-1"
                rows={2}
                data-testid="input-tema"
              />
              <p className="text-xs text-gray-500 mt-1">
                Sé específico — la IA buscará exactamente esa sección en el libro.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cantidad">Cantidad (1-20)</Label>
                <Input
                  id="cantidad"
                  type="number"
                  min={1}
                  max={20}
                  value={cantidad}
                  onChange={(e) => setCantidad(e.target.value)}
                  className="mt-1"
                  data-testid="input-cantidad"
                />
              </div>
              <div>
                <Label htmlFor="puntaje">Puntaje por pregunta</Label>
                <Input
                  id="puntaje"
                  type="number"
                  min={1}
                  max={100}
                  value={puntaje}
                  onChange={(e) => setPuntaje(e.target.value)}
                  className="mt-1"
                  data-testid="input-puntaje"
                />
              </div>
            </div>

            <div>
              <Label>Tipos de pregunta</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {[
                  { key: 'multiple', label: 'Opción Múltiple', icon: '✓' },
                  { key: 'verdadero_falso', label: 'Verdadero/Falso', icon: '✓/✗' },
                  { key: 'desarrollo', label: 'Desarrollo', icon: '📝' },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTipos((p) => ({ ...p, [t.key]: !p[t.key] }))}
                    data-testid={`tipo-${t.key}`}
                    className={`p-3 rounded-lg border-2 text-sm font-medium transition ${
                      tipos[t.key]
                        ? 'bg-indigo-100 border-indigo-500 text-indigo-900'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-lg">{t.icon}</div>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Dificultad</Label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {[
                  { key: 'facil', label: 'Fácil', color: 'green' },
                  { key: 'media', label: 'Media', color: 'blue' },
                  { key: 'dificil', label: 'Difícil', color: 'red' },
                  { key: 'mix', label: 'Mix', color: 'purple' },
                ].map((d) => (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => setDificultad(d.key)}
                    data-testid={`dif-${d.key}`}
                    className={`p-2 rounded-lg border-2 text-sm font-medium transition ${
                      dificultad === d.key
                        ? 'bg-indigo-100 border-indigo-500 text-indigo-900'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => handleClose(false)} disabled={loading}>Cancelar</Button>
              <Button
                onClick={handleGenerar}
                disabled={loading || !tema.trim()}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                data-testid="btn-generar"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando (puede tardar 30-60s)...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Generar {cantidad} pregunta(s)</>
                )}
              </Button>
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between">
              <span className="text-sm text-emerald-900">
                ✨ {preguntas.length} pregunta(s) generadas · <strong>{totalSeleccionadas}</strong> seleccionadas para agregar
              </span>
              <Button variant="outline" size="sm" onClick={() => setStep('form')} disabled={loading}>
                ← Volver
              </Button>
            </div>

            <div className="space-y-3">
              {preguntas.map((p, idx) => {
                const isSel = !!seleccionadas[p.id];
                return (
                  <div
                    key={p.id}
                    className={`rounded-lg border-2 p-4 transition ${
                      isSel ? 'border-indigo-300 bg-indigo-50/40' : 'border-gray-200 bg-gray-50 opacity-60'
                    }`}
                    data-testid={`preg-card-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={(e) => setSeleccionadas((s) => ({ ...s, [p.id]: e.target.checked }))}
                          className="w-4 h-4 accent-indigo-600"
                          data-testid={`preg-check-${idx}`}
                        />
                        <span className="text-xs font-semibold uppercase px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">
                          {p.tipo === 'multiple' ? 'Opción Múltiple' : p.tipo === 'verdadero_falso' ? 'V/F' : 'Desarrollo'}
                        </span>
                        <span className="text-xs text-gray-500">{p.puntos} pts</span>
                        {p.referencia_ia && (
                          <span className="text-xs text-gray-500 italic">· {p.referencia_ia}</span>
                        )}
                      </label>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleRegenerarUna(idx)}
                          disabled={loading}
                          className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1 px-2 py-1 rounded hover:bg-indigo-100 disabled:opacity-50"
                          title="Regenerar esta pregunta"
                          data-testid={`preg-regen-${idx}`}
                        >
                          <RefreshCw className="w-3 h-3" /> Regenerar
                        </button>
                      </div>
                    </div>

                    <Textarea
                      value={p.enunciado}
                      onChange={(e) => updatePregunta(idx, { enunciado: e.target.value })}
                      className="text-sm bg-white mb-2"
                      rows={2}
                      data-testid={`preg-enunciado-${idx}`}
                    />

                    {p.tipo === 'multiple' && p.opciones && (
                      <div className="space-y-1">
                        {p.opciones.map((op, oi) => (
                          <div key={op.letra} className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => updatePregunta(idx, { respuesta_correcta: op.letra })}
                              className={`w-7 h-7 rounded-full text-xs font-bold shrink-0 transition ${
                                p.respuesta_correcta === op.letra
                                  ? 'bg-emerald-500 text-white'
                                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                              }`}
                              data-testid={`preg-${idx}-op-${op.letra}`}
                              title="Marcar como correcta"
                            >
                              {p.respuesta_correcta === op.letra ? <Check className="w-3 h-3 mx-auto" /> : op.letra}
                            </button>
                            <Input
                              value={op.texto}
                              onChange={(e) => updateOpcion(idx, oi, e.target.value)}
                              className="text-sm bg-white"
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {p.tipo === 'verdadero_falso' && (
                      <div className="flex gap-2">
                        {['Verdadero', 'Falso'].map((v) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => updatePregunta(idx, { respuesta_correcta: v })}
                            className={`px-4 py-1.5 rounded text-sm font-medium transition ${
                              p.respuesta_correcta === v
                                ? 'bg-emerald-500 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                            data-testid={`preg-${idx}-vf-${v}`}
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    )}

                    {p.tipo === 'desarrollo' && (
                      <div>
                        <Label className="text-xs text-gray-600">Criterios de corrección (lo que debe contener la respuesta)</Label>
                        <Textarea
                          value={p.criterios || p.respuesta_correcta || ''}
                          onChange={(e) => updatePregunta(idx, { criterios: e.target.value, respuesta_correcta: e.target.value })}
                          className="text-sm bg-white mt-1"
                          rows={3}
                          data-testid={`preg-crit-${idx}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between items-center pt-4 border-t sticky bottom-0 bg-white pb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const all = preguntas.every((p) => seleccionadas[p.id]);
                  const sel = {};
                  if (!all) preguntas.forEach((p) => { sel[p.id] = true; });
                  setSeleccionadas(sel);
                }}
                data-testid="btn-toggle-all"
              >
                {preguntas.every((p) => seleccionadas[p.id]) ? <><X className="w-4 h-4 mr-1" /> Desmarcar todas</> : <><Check className="w-4 h-4 mr-1" /> Marcar todas</>}
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => handleClose(false)}>Cancelar</Button>
                <Button
                  onClick={handleAgregar}
                  disabled={totalSeleccionadas === 0}
                  className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700"
                  data-testid="btn-aceptar"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Agregar {totalSeleccionadas} pregunta{totalSeleccionadas !== 1 ? 's' : ''} a la prueba
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
